import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";

import CustomNavbar from "../../components/common/CustomNavbar";
import PageLayout from "../../components/layouts/PageLayout";

import { IoArrowBack, IoAdd } from "react-icons/io5";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import { MdOpenInNew } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import { fetchMyCompanies } from "../../features/developer/DeveoperServices";
import { CompanyDetails } from "../../types/developerTypes";
import ECSLoader from "../../components/common/EcsLoader";

export default function CompanyList() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const [companies, setCompanies] = useState<CompanyDetails[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadCompanies = async () => {
            try {
                const data = await dispatch(fetchMyCompanies()).unwrap();
                setCompanies(data);
            } catch {
                setCompanies([]);
            } finally {
                setLoading(false);
            }
        };

        loadCompanies();
    }, [dispatch]);

    return (
        <PageLayout
            customNavbar={
                <CustomNavbar>
                    <div className="flex gap-3 items-center w-max">
                        <span className="font-bold text-lg uppercase">company list</span>
                    </div>
                </CustomNavbar>
            }
        >
            <div className="bg-white rounded-4xl p-7 flex flex-col gap-6">
                <div className="flex flex-col items-end justify-end sm:flex-row gap-4 sm:items-center">

                    <button
                        onClick={() => navigate("/admin/developer/company/register")}
                        className="flex items-center justify-center px-5 py-3 rounded-4xl bg-brand-blue-gradient text-white font-bold whitespace-nowrap"
                    >
                        <IoAdd className="text-lg" />
                        Register Company
                    </button>
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">My Companies</h2>
                    <p className="text-sm text-gray-400 mt-1">
                        Manage your registered developer companies and API tokens.
                    </p>

                </div>

                <hr />

                {loading ? (
                    <ECSLoader />
                ) : companies.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-4 text-gray-400">
                        <HiOutlineOfficeBuilding className="text-5xl" />
                        <p className="text-base font-medium">No companies registered yet.</p>
                        <button
                            onClick={() => navigate("/admin/developer/company/register")}
                            className="flex items-center gap-2 px-5 py-2 bg-brand-blue-gradient text-white rounded-xl text-sm font-semibold"
                        >
                            <IoAdd className="text-lg" />
                            Register Your First Company
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead>
                                <tr className="text-gray-400 border-b text-xs uppercase tracking-wider">
                                    <th className="py-3 px-4">Company Name</th>
                                    <th className="py-3 px-4">Website</th>
                                    <th className="py-3 px-4">Status</th>
                                    <th className="py-3 px-4 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {companies.map((company) => (
                                    <tr
                                        key={company.id}
                                        className="hover:bg-gray-50 transition-colors"
                                    >
                                        <td className="py-4 px-4 font-semibold text-gray-800">
                                            {company.name}
                                        </td>

                                        <td className="py-4 px-4">
                                            <a
                                                href={
                                                    company.website.startsWith("http")
                                                        ? company.website
                                                        : `https://${company.website}`
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-500 hover:underline flex items-center gap-1 w-max"
                                            >
                                                {company.website}
                                                <MdOpenInNew className="text-xs" />
                                            </a>
                                        </td>

                                        <td className="py-4 px-4">
                                            {company.active ? (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-600 border border-green-100">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-600 border border-yellow-100">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                                                    Under Review
                                                </span>
                                            )}
                                        </td>

                                        <td className="py-4 px-4 text-right">
                                            <button
                                                onClick={() =>
                                                    navigate(`/admin/developer/company/${company.id}`)
                                                }
                                                className="text-brand-main font-semibold hover:underline text-sm"
                                            >
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </PageLayout>
    );
}